import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';

/** One selectable column. Every table's column catalogue is a list of these. */
export interface TableColumnMeta {
  id: string;
  label: string;
  category?: string;
  alwaysVisible?: boolean;
}

export interface ColumnPreset {
  name: string;
  columns: readonly string[] | 'all';
}

interface ColumnSelectorProps {
  columns: TableColumnMeta[];
  visibleColumns: Set<string>;
  onColumnToggle: (columnId: string) => void;
  onReset: () => void;
  onPresetSelect: (preset: string) => void;
  /** Presets to offer. Tables without a meaningful grouping pass none, and the
   *  preset row is then omitted. */
  presets?: Record<string, ColumnPreset>;
}

const COLUMN_PRESETS = {
  minimal: {
    name: 'Minimal',
    columns: ['name', 'status', 'lastSeen', 'actions'],
  },
  operations: {
    name: 'Operations',
    columns: ['name', 'status', 'clientVersion', 'network.ipAddress', 'lastSeen', 'actions'],
  },
  security: {
    name: 'Security',
    columns: ['name', 'registrationStatus', 'registrationId', 'secureCode', 'lastSeen', 'actions'],
  },
  full: {
    name: 'Full View',
    columns: 'all' as const, // Special case for all columns
  },
};

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns,
  onColumnToggle,
  onReset,
  onPresetSelect,
  presets,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    onColumnToggle(columnId);
  };

  const handlePresetSelect = (preset: string) => {
    onPresetSelect(preset);
  };

  // Group columns by category
  const groupedColumns = columns.reduce((acc, column) => {
    const category = column.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(column);
    return acc;
  }, {} as Record<string, TableColumnMeta[]>);

  const visibleCount = Array.from(visibleColumns).filter(
    (id) => columns.some((col) => col.id === id && !col.alwaysVisible)
  ).length;

  const totalCount = columns.filter((col) => !col.alwaysVisible).length;

  return (
    <>
      <Tooltip title="Customize columns">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 1 }}
          aria-label="customize columns"
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: { sx: { width: 320, maxHeight: 500 } },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary'
          }}>
            Columns ({visibleCount}/{totalCount})
          </Typography>
        </Box>
        <Divider />

        {/* Presets */}
        {presets && Object.keys(presets).length > 0 && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" gutterBottom sx={{
            color: 'text.secondary'
          }}>
            PRESETS
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {Object.entries(presets ?? {}).map(([key, preset]) => (
              <Button
                key={key}
                size="small"
                variant="outlined"
                onClick={() => {
                  handlePresetSelect(key);
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {preset.name}
              </Button>
            ))}
          </Box>
        </Box>
        )}
        <Divider />

        {/* Column toggles by category */}
        {Object.entries(groupedColumns).map(([category, categoryColumns]) => (
          <Box key={category}>
            <Box sx={{ px: 2, pt: 1 }}>
              <Typography variant="caption" sx={{
                color: 'text.secondary'
              }}>
                {category.toUpperCase()}
              </Typography>
            </Box>
            {categoryColumns.map((column) => (
              <MenuItem
                key={column.id}
                dense
                onClick={(e) => {
                  e.stopPropagation();
                  if (!column.alwaysVisible) {
                    handleColumnToggle(column.id);
                  }
                }}
                disabled={column.alwaysVisible}
              >
                {/*
                  The adjacent <ListItemText> names the MenuItem, not the
                  <input>. `slotProps.input` (not the deprecated `inputProps`)
                  is where MUI 7 puts attributes on the real input.
                */}
                <Checkbox
                  checked={visibleColumns.has(column.id)}
                  disabled={column.alwaysVisible}
                  size="small"
                  slotProps={{ input: { 'aria-label': `Show ${column.label} column` } }}
                />
                <ListItemText
                  primary={column.label}
                  slotProps={{
                    primary: { variant: 'body2' }
                  }}
                />
              </MenuItem>
            ))}
          </Box>
        ))}

        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Button
            size="small"
            onClick={onReset}
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Reset to default
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default ColumnSelector;
export { COLUMN_PRESETS };
